export const INTELLIGENCE_PLAN_IDS = {
  leadership_record: "leadership_record",
  leadership_record_solo: "leadership_record_solo",
  intelligence: "intelligence",
  intelligence_solo: "intelligence_solo",
  intelligence_plus: "intelligence_plus",
} as const;

export type IntelligencePlanId =
  (typeof INTELLIGENCE_PLAN_IDS)[keyof typeof INTELLIGENCE_PLAN_IDS];

export type IntelligencePlan = {
  id: IntelligencePlanId;
  label: string;
  shortLabel: string;
  priceUsdMonthly: number;
  monthlyCaptureMinutes: number;
  stripePriceEnvKey: string;
  audience: "one_leader_engagement" | "multi_leader_engagement";
};

/**
 * Coach-led engagement plans.
 *
 * Phase 5 adds one-active-leader coach pricing as first-class engagement plan
 * IDs. These are intentionally separate from the existing self-serve solo
 * leader subscription path so coach engagement checkout, solo checkout, and
 * existing engagements cannot silently reprice each other.
 */
export const INTELLIGENCE_PLANS = {
  leadership_record: {
    id: INTELLIGENCE_PLAN_IDS.leadership_record,
    label: "Leadership Record",
    shortLabel: "Record",
    priceUsdMonthly: 25,
    monthlyCaptureMinutes: 1200,
    stripePriceEnvKey: "STRIPE_LEADERSHIP_RECORD_SEAT_PRICE_ID",
    audience: "multi_leader_engagement",
  },
  leadership_record_solo: {
    id: INTELLIGENCE_PLAN_IDS.leadership_record_solo,
    label: "Leadership Record Solo",
    shortLabel: "Record Solo",
    priceUsdMonthly: 19,
    monthlyCaptureMinutes: 600,
    stripePriceEnvKey: "STRIPE_LEADERSHIP_RECORD_SOLO_SEAT_PRICE_ID",
    audience: "one_leader_engagement",
  },
  intelligence: {
    id: INTELLIGENCE_PLAN_IDS.intelligence,
    label: "Leadership and Coaching Intelligence",
    shortLabel: "Intelligence",
    priceUsdMonthly: 59,
    monthlyCaptureMinutes: 3000,
    stripePriceEnvKey: "STRIPE_INTELLIGENCE_SEAT_PRICE_ID",
    audience: "multi_leader_engagement",
  },
  intelligence_solo: {
    id: INTELLIGENCE_PLAN_IDS.intelligence_solo,
    label: "Intelligence Solo",
    shortLabel: "Intelligence Solo",
    priceUsdMonthly: 39,
    monthlyCaptureMinutes: 600,
    stripePriceEnvKey: "STRIPE_INTELLIGENCE_SOLO_SEAT_PRICE_ID",
    audience: "one_leader_engagement",
  },
  intelligence_plus: {
    id: INTELLIGENCE_PLAN_IDS.intelligence_plus,
    label: "Leadership and Coaching Intelligence Plus",
    shortLabel: "Intelligence Plus",
    priceUsdMonthly: 99,
    monthlyCaptureMinutes: 9000,
    stripePriceEnvKey: "STRIPE_INTELLIGENCE_PLUS_SEAT_PRICE_ID",
    audience: "multi_leader_engagement",
  },
} as const satisfies Record<IntelligencePlanId, IntelligencePlan>;

export const ONE_LEADER_ENGAGEMENT_PLAN_IDS = [
  INTELLIGENCE_PLAN_IDS.leadership_record_solo,
  INTELLIGENCE_PLAN_IDS.intelligence_solo,
] as const satisfies readonly IntelligencePlanId[];

export const MULTI_LEADER_ENGAGEMENT_PLAN_IDS = [
  INTELLIGENCE_PLAN_IDS.leadership_record,
  INTELLIGENCE_PLAN_IDS.intelligence,
  INTELLIGENCE_PLAN_IDS.intelligence_plus,
] as const satisfies readonly IntelligencePlanId[];

export const DEFAULT_ONE_LEADER_ENGAGEMENT_PLAN_ID = INTELLIGENCE_PLAN_IDS.intelligence_solo;
export const DEFAULT_MULTI_LEADER_ENGAGEMENT_PLAN_ID = INTELLIGENCE_PLAN_IDS.intelligence;

export function isOneLeaderEngagementPlanId(planId: IntelligencePlanId): boolean {
  return INTELLIGENCE_PLANS[planId].audience === "one_leader_engagement";
}

export function getEngagementPlanOptionsForActiveLeaderCount(activeLeaderCount: number): readonly IntelligencePlanId[] {
  return activeLeaderCount === 1 ? ONE_LEADER_ENGAGEMENT_PLAN_IDS : MULTI_LEADER_ENGAGEMENT_PLAN_IDS;
}

export function getDefaultEngagementPlanIdForActiveLeaderCount(activeLeaderCount: number): IntelligencePlanId {
  return activeLeaderCount === 1
    ? DEFAULT_ONE_LEADER_ENGAGEMENT_PLAN_ID
    : DEFAULT_MULTI_LEADER_ENGAGEMENT_PLAN_ID;
}

export const CAPTURE_HOUR_PACK = {
  label: "+5 capture hours",
  priceUsd: 29,
  captureMinutes: 300,
  stripePriceEnvKey: "STRIPE_CAPTURE_HOUR_PACK_PRICE_ID",
} as const;

export function resolveIntelligencePlanByStripePriceEnvKey(envKey: string) {
  return Object.values(INTELLIGENCE_PLANS).find((plan) => plan.stripePriceEnvKey === envKey) ?? null;
}
